import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostBinding, HostListener, OnDestroy, ViewChild } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { ConnectionManager } from '@app/utilities/foblex-flow/connection-manager';
import { FlowNode, NodeManager } from '@app/utilities/foblex-flow/node-manager';
import { FReassignConnectionEvent } from '@foblex/flow';
import { WebClient } from '@injectables';
import { Device } from '@models';
import { Observable, combineLatest, distinctUntilChanged, map } from 'rxjs';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { DeviceNode } from './nodes/device-node/device-node.component';
import { NodesComponent } from './nodes/nodes.component';

interface DeviceComparisonContext {
    devices: Device[];
    upstreamDeviceIds: Record<number, number>;
}

@Component({
    selector: 'flow-board',
    templateUrl: './flow-board.component.html',
    styleUrls: ['./flow-board.component.scss']
})
export class FlowBoardComponent implements AfterViewInit, OnDestroy {

    public readonly nodeClient = this.webClient.model.node;
    public readonly nodes$ = this.fragQuery.nodes$;
    public readonly comparisonContext$: Observable<DeviceComparisonContext> = combineLatest([
        this.nodeManager.nodes$,
        this.connectionManager.connections$,
    ]).pipe(
        map(([nodes, connections]) => {
            const deviceNodes = nodes.filter(DeviceNode.isDeviceNode);
            const upstreamDeviceIds: Record<number, number> = {};

            connections.forEach(connection => {
                const upstreamNode = deviceNodes.find(node => node.outputId === connection.from);
                const downstreamNode = deviceNodes.find(node => node.inputId === connection.to);
                if (
                    upstreamNode
                    && downstreamNode
                    && upstreamNode.device.id !== downstreamNode.device.id
                ) {
                    upstreamDeviceIds[downstreamNode.device.id] = upstreamNode.device.id;
                }
            });
            return {
                devices: deviceNodes.map(node => node.device),
                upstreamDeviceIds,
            };
        }),
        distinctUntilChanged((previous, current) =>
            previous.devices.length === current.devices.length
            && previous.devices.every(
                (device, index) => device.id === current.devices[index].id
            )
            && previous.devices.every(
                device => previous.upstreamDeviceIds[device.id]
                    === current.upstreamDeviceIds[device.id]
            )
        ),
    );
    public nodes: FlowNode[] = [];
    public loading$ = this.fragQuery.nodesLoading$
    public comparisonPanelHeight = 315;
    @HostBinding('style.height.px')
    public flowBoardHeight: number | null = null;
    private isResizingComparison = false;
    private resizeStartY = 0;
    private resizeStartHeight = 0;
    private resizeAnimationFrame: number | null = null;
    private viewportMeasureAnimationFrame: number | null = null;

    constructor(
        private readonly dialog: DialogService,
        private readonly fragQuery: FragQueryService,
        private readonly webClient: WebClient,
        private readonly snackBar: MatSnackBar,
        private readonly nodeManager: NodeManager,
        private readonly connectionManager: ConnectionManager,
        private readonly elementRef: ElementRef<HTMLElement>,
        private readonly changeDetectorRef: ChangeDetectorRef,
    ) {}

    @ViewChild(NodesComponent)
    public nodesComponent!: NodesComponent;

    public ngAfterViewInit(): void {
        // Router content and the toolbar finish positioning after this lifecycle hook.
        // Measure on the next frame so the board uses its final viewport offset.
        this.scheduleFlowBoardMeasurement();
    }

    public ngOnDestroy(): void {
        if (this.resizeAnimationFrame != null) {
            cancelAnimationFrame(this.resizeAnimationFrame);
        }
        if (this.viewportMeasureAnimationFrame != null) {
            cancelAnimationFrame(this.viewportMeasureAnimationFrame);
        }
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }

    public zoomIn() { this.nodesComponent.zoomIn(); }
    public zoomOut() { this.nodesComponent.zoomOut(); }
    public resetZoom() { this.nodesComponent.resetZoom(); }

    @HostListener('window:resize')
    public scheduleFlowBoardMeasurement(): void {
        if (this.viewportMeasureAnimationFrame != null) {
            cancelAnimationFrame(this.viewportMeasureAnimationFrame);
        }

        this.viewportMeasureAnimationFrame = requestAnimationFrame(() => {
            this.updateFlowBoardHeight();
            this.viewportMeasureAnimationFrame = null;
        });
    }

    private updateFlowBoardHeight(): void {
        const hostTop = Math.max(
            0,
            this.elementRef.nativeElement.getBoundingClientRect().top,
        );
        this.flowBoardHeight = Math.max(0, window.innerHeight - hostTop);
        this.comparisonPanelHeight = this.clampComparisonHeight(
            this.comparisonPanelHeight,
        );
        this.changeDetectorRef.detectChanges();
    }

    public async reassignConnection(event: FReassignConnectionEvent): Promise<void> {
        if (!event.newFInputId) {
            return;
        }
        try {
            await this.connectionManager.reassignConnection(
                event.fOutputId,
                event.oldFInputId,
                event.newFInputId,
            );
        } catch (error) {
            console.error('Failed to reassign connection:', error);
            this.snackBar.open('Failed to reassign connection.', 'OK', {
                duration: 5000,
            });
        }
    }

    public async saveFlow() {
        this.nodes = this.nodeManager.getNodes();
        if (!this.nodes) return;

        const deviceNodeUpdateRequests = this.getdeviceNodeUpdatePromises()

        try {
            await this.dialog.await(Promise.all(deviceNodeUpdateRequests));
            this.fragQuery.clearCachedPositions();
            this.fragQuery.trigger$.next();
            this.snackBar.open('Flow saved successfully', 'OK', {
                duration: 5000,
                panelClass: ['snackbar-success'],
            });
        } catch (error) {
            console.error('Failed to save some nodes:', error);
        }
    }

    private getdeviceNodeUpdatePromises = (): Promise<Boolean>[] => {
        const saveRequests = this.nodes.map((node) => {
            if(DeviceNode.isDeviceNode(node)) {
                return this.nodeClient.save(node.toNode());
            }else{
                return undefined;
            }
        });
        return saveRequests;
    }

    public openAddNodeDialog() {
        this.dialog.openCreateNodeDialog()
    }

    public startComparisonResize(event: PointerEvent): void {
        event.preventDefault();
        this.isResizingComparison = true;
        this.resizeStartY = event.clientY;
        this.resizeStartHeight = this.comparisonPanelHeight;
        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';
    }

    @HostListener('document:pointermove', ['$event'])
    public resizeComparison(event: PointerEvent): void {
        if (!this.isResizingComparison) {
            return;
        }

        const upwardDistance = this.resizeStartY - event.clientY;
        this.comparisonPanelHeight = this.clampComparisonHeight(
            this.resizeStartHeight + upwardDistance,
        );
        this.scheduleChartReflow();
    }

    @HostListener('document:pointerup')
    @HostListener('window:blur')
    public stopComparisonResize(): void {
        if (!this.isResizingComparison) {
            return;
        }

        this.isResizingComparison = false;
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        this.scheduleChartReflow();
    }

    private clampComparisonHeight(height: number): number {
        const availableHeight = this.flowBoardHeight
            ?? this.elementRef.nativeElement.clientHeight;
        const rootFontSize = Number.parseFloat(
            getComputedStyle(document.documentElement).fontSize,
        ) || 16;
        // Below this height Highcharts cannot fit both axis titles and the compact controls
        // without clipping them. Keep the draggable panel compact, but preserve its content.
        const minimumHeight = 15.5 * rootFontSize;
        const maximumHeight = 30 * rootFontSize;
        // Reserve space for the canvas, resize handle, and Add Node / Save Flow footer.
        const reservedHeight = 25 * rootFontSize;
        const availableComparisonHeight = Math.max(
            minimumHeight,
            Math.min(maximumHeight, availableHeight - reservedHeight),
        );
        return Math.min(
            availableComparisonHeight,
            Math.max(minimumHeight, height),
        );
    }

    private scheduleChartReflow(): void {
        if (this.resizeAnimationFrame != null) {
            cancelAnimationFrame(this.resizeAnimationFrame);
        }

        this.resizeAnimationFrame = requestAnimationFrame(() => {
            window.dispatchEvent(new Event('resize'));
            this.resizeAnimationFrame = null;
        });
    }
}
